// PeriodCloseWizard — QuickBooks-style 3-step period close.
//
//   Step 1: Readiness checklist (bank reconciled, bills approved, JEs
//           posted, invoices sent, expenses approved, trial balance tied)
//   Step 2: Trial balance preview — quick sanity check of period end
//   Step 3: Confirm with reason, choose Soft Close → Hard Close
//
// Mountable as a modal; takes a period object and fires onClosed when
// the close succeeds.
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Stack, Button, IconButton, Chip, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
    Stepper, Step, StepLabel, TextField, Divider,
    Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const fmtMoney = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));
};

export default function PeriodCloseWizard({ open, period, onClose, onClosed }) {
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [tb, setTb] = useState(null);
    const [reason, setReason] = useState('');
    const [closing, setClosing] = useState(false);
    const [forceContinue, setForceContinue] = useState(false);

    const loadReadiness = useCallback(async () => {
        if (!period) return;
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/fiscal-periods/${period.id}/readiness`);
            setReadiness(res.data?.data || null);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [period]);

    const loadTrialBalance = useCallback(async () => {
        if (!period) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/reports/trial-balance', {
                params: { as_of_date: period.end_date },
            });
            setTb(res.data || null);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        if (open) {
            setStep(0);
            setReason('');
            setForceContinue(false);
            setError(null);
            loadReadiness();
        }
    }, [open, loadReadiness]);

    useEffect(() => {
        if (open && step === 1 && !tb) loadTrialBalance();
    }, [open, step, tb, loadTrialBalance]);

    const doClose = async () => {
        setClosing(true); setError(null);
        try {
            const res = await axiosInstance.post(`/api/finance/fiscal-periods/${period.id}/close`, {
                reason, force: forceContinue,
            });
            if (res.data?.success) {
                onClosed?.(res.data.data);
                onClose();
            } else {
                setError(res.data?.message || 'Close failed');
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setClosing(false);
        }
    };

    const canProceedFromStep0 = readiness?.ready_to_close || forceContinue;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                Close {period?.name || `period ending ${period?.end_date}`}
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={step} sx={{ mb: 3 }}>
                    <Step><StepLabel sx={{ fontFamily }}>Readiness</StepLabel></Step>
                    <Step><StepLabel sx={{ fontFamily }}>Trial Balance</StepLabel></Step>
                    <Step><StepLabel sx={{ fontFamily }}>Confirm</StepLabel></Step>
                </Stepper>

                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* Step 0 — Readiness */}
                {step === 0 && readiness && (
                    <Stack spacing={1}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: readiness.ready_to_close ? '#f0fdf4' : '#fef2f2' }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {readiness.ready_to_close
                                    ? <CheckCircleIcon sx={{ color: ACCENT, fontSize: 28 }} />
                                    : <CancelIcon sx={{ color: '#ef4444', fontSize: 28 }} />
                                }
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 700 }}>
                                        {readiness.ready_to_close
                                            ? 'All checks passed — period is ready to close'
                                            : `${readiness.failed_count} of ${readiness.total_checks} checks need attention`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                        Period: {readiness.period.start_date} → {readiness.period.end_date}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Table size="small">
                            <TableBody>
                                {(readiness.checks || []).map((c) => (
                                    <TableRow key={c.key}>
                                        <TableCell sx={{ width: 40 }}>
                                            {c.ok
                                                ? <CheckCircleIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                                : <CancelIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily, fontWeight: 600 }}>
                                                {c.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                                {c.detail}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {c.count > 0 && !c.ok && (
                                                <Chip size="small" color="warning"
                                                      label={c.key === 'trial_balance' ? `${(c.count/100).toFixed(2)} off`
                                                                                        : `${c.count} issues`} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {!readiness.ready_to_close && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily }}>
                                    You can fix these items first (recommended), or force-close anyway.
                                    Force-closing will hide the unresolved items from reports but they
                                    remain in the underlying data.
                                </Typography>
                                <Button size="small" sx={{ mt: 1 }}
                                        onClick={() => setForceContinue(true)}
                                        disabled={forceContinue}>
                                    {forceContinue ? 'Force-close enabled' : 'Force-close anyway'}
                                </Button>
                            </Alert>
                        )}
                    </Stack>
                )}

                {/* Step 1 — Trial Balance */}
                {step === 1 && tb && (
                    <Stack spacing={1}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                        Trial balance as of {tb.as_of_date}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>
                                        {tb.is_balanced ? 'In balance' : 'OUT OF BALANCE'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                        Total Debits
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#10b981' }}>
                                        {fmtMoney(tb.total_debits)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                        Total Credits
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#ef4444' }}>
                                        {fmtMoney(tb.total_credits)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Account</TableCell>
                                        <TableCell align="right">Debit</TableCell>
                                        <TableCell align="right">Credit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(tb.data || []).map((r) => (
                                        <TableRow key={r.account_id}>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, mr: 1 }}>
                                                    {r.account_number}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily, display: 'inline' }}>
                                                    {r.account_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {r.debit_balance > 0 ? fmtMoney(r.debit_balance) : ''}
                                            </TableCell>
                                            <TableCell align="right">
                                                {r.credit_balance > 0 ? fmtMoney(r.credit_balance) : ''}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Stack>
                )}

                {/* Step 2 — Confirm */}
                {step === 2 && (
                    <Stack spacing={2}>
                        <Alert severity="info">
                            Closing will transition this period from <strong>{period?.status}</strong> to
                            {' '}<strong>{period?.status === 'OPEN' ? 'SOFT_CLOSED' : 'HARD_CLOSED'}</strong>.
                            Posting to soft-closed periods requires override; hard-closed blocks all new JEs.
                        </Alert>
                        <TextField
                            label="Close reason / memo"
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Reviewed all accruals, depreciation posted, bank recs signed off…"
                        />
                        {forceContinue && (
                            <Alert severity="warning">
                                Force-close is ON. Unresolved checklist items will be skipped.
                            </Alert>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Box sx={{ flexGrow: 1 }} />
                {step > 0 && <Button onClick={() => setStep(s => s - 1)}>Back</Button>}
                {step < 2 && (
                    <Button
                        variant="contained"
                        onClick={() => setStep(s => s + 1)}
                        disabled={step === 0 && !canProceedFromStep0}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        Next
                    </Button>
                )}
                {step === 2 && (
                    <Button
                        variant="contained"
                        onClick={doClose}
                        disabled={closing}
                        startIcon={closing ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {closing ? 'Closing…' : `Close period`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
